/**
 * Copyright © 2026 Adnuntius AS.
 */
import {AdnListHelperService} from '../common/controller/list-helper.service';
import {ChangeDetectorRef, Component, DoCheck, Inject, Input, NgZone, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {NgForm} from '@angular/forms';
import {safeDetectChanges} from '../common/util/safe-detect-changes';
import {deferMarkForCheck} from '../common/util/defer-mark-for-check';
import {columnFinder} from '../common/controller/resource-list-utils';
import {convertIabCategoryToString} from '../../components/util/iab-taxonomy';
import {LocalNetworkInfo} from '../../components/session/local-network-info';
import {adTagLinker} from '../ad-unit/ad-tag-linker';
import {AdUnitBulkActionService} from '../ad-unit/ad-unit-bulk-action.service';
import {resetDimensionsBasedOnAdUnitOption} from '../ad-unit/ad-unit-commons';
import {ContentSummaryHelper} from '../../components/util/content-summary-helper.service';
import {RecentArticlesSearchResource} from '../../components/api/resources/resource/recent-articles';
import {CrawlerResource} from '../../components/api/resources/resource/crawler';
import * as _ from 'lodash';
import template from './site-analysis-page.component.html';
import {AdUnitResource} from '../../components/api/resources/resource/adunit.resource';
import {ConsentStatisticResource} from '../../components/api/resources/resource/site.resource';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {LocalUserProfile} from '../../components/session/local-user-profile';
import {visiblePageNumbers} from '../common/directives/pagination/visible-page-numbers';
import {StateService} from '@uirouter/core';

type AnalysisTab = 'adUnits' | 'charts' | 'adUnitsCharts' | 'profiles' | 'reports' | 'traffic' | 'location' | 'availability' | 'explore';

interface AnalysisListColumn {
  param: string;
  label?: string;
  labelInstant?: string;
  numeric?: boolean;
  sortable?: boolean;
}

@Component({
  standalone: false,
  selector: 'adn-site-analysis-page',
  template
})
export class SiteAnalysisPageComponent implements DoCheck, OnChanges, OnDestroy, OnInit {
  @Input() model: any;
  @Input() modelComms: any;
  @Input() isNew: boolean;

  effectiveIsNew = false;
  apiModel: any;
  activeTab: AnalysisTab = 'adUnits';

  readonly purposeMatrix: Record<number, string> = {
    1: 'Store and/or access information on a device',
    2: 'Select basic ads',
    3: 'Create a personalized ads profile',
    4: 'Select personalized ads',
    7: 'Measure ad performance'
  };
  private readonly relevantPurposes = [1, 2, 4];

  auFields: AnalysisListColumn[] = [];
  adUnits: any = {};
  /** Snapshot — list-helper replaces adUnits.items on settle (NG0100). */
  displayItems: any[] = [];
  hasItems = false;
  adUnit: any;
  showAdUnitForm = false;
  adUnitsEmpty = true;
  adUnitsCount = 0;
  selAdUnit: any;

  showAdCount = false;
  showNotes = false;
  adminUserName = '';
  canSeeReports = false;
  isPureMarketplace = false;
  showConsent = false;
  showSemantic = false;
  showContentProfile = -1;

  theUrl = '';
  queryRunning = false;
  info: any;
  infoError = false;
  triggerDone = false;
  triggerMessage: number | null = null;

  adCountRunning = false;
  adCount: any;
  adCountsRunning = false;
  adCounts: any;
  recentArticlesRunning = false;
  recentArticles: any[];
  viewedArticlesRunning = false;
  viewedArticles: any[];
  isRunning = false;
  noConsentResults = false;
  consent: any;
  siteHost = '';

  showingChart = '';
  showingTraffic = false;
  showingLocation = false;
  showingReach = false;
  showingExplorer = false;
  visibleReports = false;

  readonly adUnitItemStateName = 'app.inv.ad-units.ad-unit';
  adTagGenerator: () => void;
  bulkAction: () => void;

  private lastLoadedModelId: string | undefined;
  private subscribedModelComms: any;
  private destroyed = false;
  private adUnitsInitialized = false;

  constructor(
    private readonly $state: StateService,
    @Inject(LocalUserPermissions) LocalUserPermissions: any,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    @Inject(LocalUserProfile) localUserProfile: any,
    private readonly adnListHelper: AdnListHelperService,
    @Inject(AdUnitResource) private readonly AdUnit: any,
    private readonly contentSummaryHelper: ContentSummaryHelper,
    private readonly recentArticlesSearchResource: RecentArticlesSearchResource,
    private readonly crawlerResource: CrawlerResource,
    @Inject(ConsentStatisticResource) private readonly ConsentStatistic: any,
    private readonly adUnitBulkActionService: AdUnitBulkActionService,
    private readonly ngZone: NgZone,
    private readonly cdr: ChangeDetectorRef
  ) {
    const isSpecialUser = localUserProfile.get('username') === 'adnuntius@adnuntius.com'
      || localUserProfile.get('username') === 'broker1@adnuntius.com';
    if (isSpecialUser) {
      this.showAdCount = true;
      this.showNotes = true;
      this.adminUserName = localUserProfile.get('username');
    }
    this.canSeeReports = LocalUserPermissions.getAnyVisibility().RUN_REPORTS === true
      || LocalUserPermissions.getAnyVisibility().AX_PUBLISHER === true;
    this.isPureMarketplace = LocalNetworkProfile.isPureMarketplacePlatform();
    this.showConsent = LocalNetworkProfile.isPureMarketplacePlatform();
    this.showSemantic = LocalNetworkProfile.isSemanticPlatform();
    this.auFields = [
      {param: 'name'},
      {param: '', label: 'dimensions', sortable: false},
      {param: 'requestedInLastDay', label: 'status'},
      {param: '', label: 'tagId', sortable: false}
    ];
    if (this.LocalNetworkProfile.isPureMarketplacePlatform() && isSpecialUser) {
      this.auFields.push({label: 'isFullPage', param: 'fullPage'});
    }
    this.adUnits = {};
    this.adTagGenerator = () => undefined;
    this.bulkAction = () => undefined;
  }

  get showNewMessage(): boolean {
    return !!this.effectiveIsNew;
  }

  ngOnInit(): void {
    this.effectiveIsNew = !!this.isNew;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isNew) {
      this.effectiveIsNew = !!this.isNew;
    }
    if (changes.model) {
      this.apiModel = this.model;
      this.updateSiteHost();
      this.canDoContentProfiles();
    }
    if (changes.modelComms) {
      this.subscribeModelComms();
    }
    this.refreshAdUnitsIfNeeded();
  }

  ngDoCheck(): void {
    const modelId = this.model?.id;
    if (modelId && modelId !== this.lastLoadedModelId && !this.effectiveIsNew) {
      this.refreshAdUnitsIfNeeded();
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
  }

  selectTab(tab: AnalysisTab): void {
    this.activeTab = tab;
    if (tab === 'charts') {
      this.showingChart = 'SITE';
    } else if (tab === 'adUnitsCharts') {
      this.showingChart = 'AD_UNIT';
    } else if (tab === 'profiles') {
      this.queryConsent();
      this.canDoContentProfiles();
    } else if (tab === 'reports') {
      this.visibleReports = true;
    } else if (tab === 'traffic') {
      this.showingTraffic = true;
    } else if (tab === 'location') {
      this.showingLocation = true;
    } else if (tab === 'availability') {
      this.showingReach = true;
    } else if (tab === 'explore') {
      this.showingExplorer = true;
    }
  }

  hasColumn(param: string): boolean {
    return columnFinder({auFields: this.auFields}, 'auFields', param);
  }

  columnLabel(column: AnalysisListColumn): string {
    if (column.labelInstant) {
      return column.labelInstant;
    }
    const key = column.label || column.param;
    return key ? 'label.' + key : '';
  }

  isColumnSortable(column: AnalysisListColumn): boolean {
    return column.sortable !== false && !!column.param;
  }

  sortBy(column: AnalysisListColumn): void {
    if (!this.isColumnSortable(column) || !this.adUnits?.makeSortQuery) {
      return;
    }
    if (column.numeric) {
      this.adUnits.makeSortQuery(column.param, column.numeric);
    } else {
      this.adUnits.makeSortQuery(column.param);
    }
  }

  changePage(page: number): void {
    if (!this.adUnits || page < 1 || page > this.totalPages() || page === this.adUnits.currentPage) {
      return;
    }
    this.adUnits.currentPage = page;
    this.adUnits.changePage();
  }

  totalPages(): number {
    return Math.max(1, Math.ceil((this.adUnits?.totalCount || 0) / (this.adUnits?.pageSize || 1)));
  }

  pageNumbers(): number[] {
    return visiblePageNumbers(this.adUnits?.currentPage || 1, this.totalPages());
  }

  pageRangeStart(): number {
    return this.adUnits.pageSize * (this.adUnits.currentPage - 1) + 1;
  }

  pageRangeEnd(): number {
    return Math.min(this.adUnits.pageSize * this.adUnits.currentPage, this.adUnits.totalCount);
  }

  loadAdUnitForm(): void {
    this.adUnit = this.AdUnit.create({
      site: this.model
    });
    this.showAdUnitForm = true;
    this.adUnit.dimensions = _.isArray(this.adUnit.dimensions) ? this.adUnit.dimensions : [[]];
    this.adUnit.dimensions[0] = _.isArray(this.adUnit.dimensions[0]) ? this.adUnit.dimensions[0] : [];
    this.adUnit.renderOption = this.adUnit.renderOption || null;
    const isFixed = _.get(this.adUnit, ['dimensions', 0, 0]) >= 0 && _.get(this.adUnit, ['dimensions', 0, 1]) >= 0;
    this.adUnit.adUnitOption = this.adUnit.adUnitOption || (isFixed ? 'FIXED' : 'RANGE');
  }

  saveAdUnit(editForm: NgForm): void {
    if (editForm.invalid || this.adUnits.query?.$pending) {
      return;
    }
    this.onAdUnitBeforeSubmit();
    this.adUnit.$save().then((saved: any) => {
      this.ngZone.run(() => {
        this.showAdUnitForm = false;
        if (!_.isArray(this.adUnits.items)) {
          this.adUnits.items = [];
        }
        this.adUnits.addItemToList?.(saved);
        deferMarkForCheck(this.cdr, () => {
          this.snapshotAdUnits();
        });
      });
    });
  }

  removeDimensions(index: number): void {
    _.pullAt(this.adUnit.dimensions, index);
  }

  addDimensions(): void {
    this.adUnit.dimensions = this.adUnit.dimensions || [];
    this.adUnit.dimensions.push([]);
  }

  onAdUnitBeforeSubmit(): void {
    if (this.adUnit.adUnitOption === 'RANGE') {
      this.adUnit.dimensions = null;
    } else {
      this.adUnit.minWidth = null;
      this.adUnit.minHeight = null;
      this.adUnit.height = null;
      this.adUnit.width = null;
      resetDimensionsBasedOnAdUnitOption(this.adUnit);
    }
  }

  queryUrl(): void {
    this.queryRunning = true;
    this.info = {};
    this.contentSummaryHelper.getProfile(this.model.id, this.theUrl).then((profileInfo: any) => {
      this.ngZone.run(() => {
        this.queryRunning = false;
        this.infoError = false;
        if (_.get(profileInfo, ['parameters', 'errorCode']) === 'ECFSR001') {
          this.infoError = true;
        } else if (_.get(profileInfo, ['content'])) {
          profileInfo.contentTypes = [];
          _.forEach(profileInfo.content, (val: any, key: string) => {
            if (key === 'categories') {
              profileInfo.categoriesOutput = _.map(val, (cat: any) => convertIabCategoryToString(cat)).join(', ');
            } else {
              profileInfo[key + 'Output'] = val.join(', ');
            }
            profileInfo.contentTypes.push(key);
          });
        }
        this.info = profileInfo;
        safeDetectChanges(this.cdr);
      });
    });
  }

  getAdCount(): void {
    this.adCountRunning = true;
    this.adCount = {};
    this.contentSummaryHelper.getAdCount(this.model.id).then((count: any) => {
      this.ngZone.run(() => {
        this.adCountRunning = false;
        this.adCount = count;
        safeDetectChanges(this.cdr);
      });
    });
  }

  getAdCounts(): void {
    this.adCountsRunning = true;
    this.adCounts = {};
    this.contentSummaryHelper.getAdCounts(this.model.id).then((counts: any) => {
      this.ngZone.run(() => {
        this.adCountsRunning = false;
        this.adCounts = counts;
        safeDetectChanges(this.cdr);
      });
    });
  }

  canDoContentProfiles(): void {
    if (!this.model?.siteUrl) {
      this.showContentProfile = 0;
      return;
    }
    this.showContentProfile = LocalNetworkInfo.obtain().getInfo('contextServiceConfigurationCount');
  }

  getRecentArticles(): void {
    this.recentArticlesRunning = true;
    this.recentArticlesSearchResource.query({
      domain: this.getDomainFromUrl(this.model.siteUrl),
      site: this.model.id,
      maxAgeInHours: 24
    }).then((page: any) => {
      this.ngZone.run(() => {
        this.recentArticles = page.results;
        _.forEach(this.recentArticles, (article: any) => {
          article.keywordsRead = article.keywords.join(', ');
        });
        this.recentArticlesRunning = false;
        safeDetectChanges(this.cdr);
      });
    }).catch(() => {
      this.ngZone.run(() => {
        this.recentArticlesRunning = false;
      });
    });
  }

  getViewedArticles(): void {
    this.viewedArticlesRunning = true;
    this.recentArticlesSearchResource.viewed({
      domain: this.getDomainFromUrl(this.model.siteUrl),
      site: this.model.id
    }).then((page: any) => {
      this.ngZone.run(() => {
        this.viewedArticles = page.results;
        _.forEach(this.viewedArticles, (article: any) => {
          article.keywordsRead = article.keywords.join(', ');
        });
        this.viewedArticlesRunning = false;
        safeDetectChanges(this.cdr);
      });
    }).catch(() => {
      this.ngZone.run(() => {
        this.viewedArticlesRunning = false;
      });
    });
  }

  triggerProfileRefresh(): void {
    this.triggerDone = true;
    this.crawlerResource.refresh(this.theUrl).then(() => {
      this.ngZone.run(() => {
        this.triggerMessage = 1;
        this.triggerDone = false;
        safeDetectChanges(this.cdr);
      });
    }).catch(() => {
      this.ngZone.run(() => {
        this.triggerMessage = 0;
        this.triggerDone = false;
        safeDetectChanges(this.cdr);
      });
    });
  }

  queryConsent(): void {
    if (!this.model?.siteUrl) {
      return;
    }
    this.noConsentResults = false;
    if (!this.siteHost) {
      this.noConsentResults = true;
    }
    this.isRunning = true;
    this.ConsentStatistic.query({site: this.model.id}, (data: any) => {
      this.ngZone.run(() => {
        this.consent = data;
        if (_.get(this.consent, ['purpose'])) {
          this.consent.purpose = _.pick(this.consent.purpose, this.relevantPurposes);
        }
        _.forEach(_.get(this.consent, ['purpose']), (p: any) => {
          p.maxPercent = Math.max(p.consentedPercent, p.legitimateInterestPercent);
          p.totalUsersPercent = this.consent.percentWithConsentString * p.maxPercent / 100;
          p.htmlClass = p.totalUsersPercent > 94.9 ? 'text-success' : p.totalUsersPercent > 74.9 ? 'text-warning' : 'text-danger';
          p.failures = [];
          if (this.consent.percentWithConsentString < 99.95) {
            p.failures.push({percentage: (100 - this.consent.percentWithConsentString), reason: 'without a consent string'});
          }
          if (p.consentedPercent > p.legitimateInterestPercent && p.consentedPercent < 99.95) {
            p.failures.push({percentage: (100 - p.consentedPercent), reason: 'did not consent'});
          }
          if (p.legitimateInterestPercent > p.consentedPercent && p.legitimateInterestPercent < 99.95) {
            p.failures.push({percentage: (100 - p.legitimateInterestPercent), reason: 'opted out of legitimate interest'});
          }
        });
        if (_.get(this.consent, ['purpose'])) {
          _.forEach(this.relevantPurposes, (purposeKey: number) => {
            if (!this.consent.purpose[purposeKey]) {
              this.consent.purpose[purposeKey] = {
                totalUsersPercent: 0,
                htmlClass: 'text-danger',
                failures: [{percentage: 100, reason: 'without a value at all'}]
              };
            }
          });
        }
        this.isRunning = false;
        safeDetectChanges(this.cdr);
      });
    }, () => {
      this.ngZone.run(() => {
        this.noConsentResults = true;
        this.isRunning = false;
        safeDetectChanges(this.cdr);
      });
    });
  }

  copyTagId(tagId: string): void {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(tagId);
    }
  }

  purposeKeys(): number[] {
    if (!this.consent?.purpose) {
      return [];
    }
    return _.map(_.keys(this.consent.purpose), (key) => parseInt(key, 10));
  }

  private subscribeModelComms(): void {
    if (!this.modelComms || this.subscribedModelComms === this.modelComms) {
      return;
    }
    this.subscribedModelComms = this.modelComms;
    this.modelComms.addSubscriber((data: any, type: any, isNewFromComms?: boolean) => {
      if (this.destroyed || type) {
        return;
      }
      this.ngZone.run(() => {
        if (isNewFromComms === true || isNewFromComms === false) {
          this.effectiveIsNew = isNewFromComms;
        } else if (_.get(data, 'name')) {
          this.effectiveIsNew = false;
        }
        if (!type && data?.name) {
          this.model = data;
          this.updateSiteHost();
          this.canDoContentProfiles();
          this.refreshAdUnitsIfNeeded(true);
        }
        safeDetectChanges(this.cdr);
      });
    });
  }

  private refreshAdUnitsIfNeeded(force = false): void {
    const modelId = this.effectiveIsNew ? undefined : this.model?.id;
    if (!force && modelId === this.lastLoadedModelId) {
      return;
    }
    this.lastLoadedModelId = modelId;
    this.initAdUnitsList();
  }

  private initAdUnitsList(): void {
    if (!this.model?.id || this.effectiveIsNew) {
      this.adUnits = {};
      this.adUnitsInitialized = false;
      this.displayItems = [];
      this.hasItems = false;
      this.adUnitsEmpty = true;
      this.adUnitsCount = 0;
      return;
    }
    if (this.adUnitsInitialized && this.adUnits?.moreParams?.site === this.model.id) {
      return;
    }
    this.adUnitsInitialized = true;
    this.adUnits = {};
    this.displayItems = [];
    this.hasItems = false;
    this.adnListHelper.setUpBasicList(this.adUnits, this.AdUnit, 'fullAdUnitsforSite', {
      moreParams: {site: this.model.id},
      afterFill: () => {
        deferMarkForCheck(this.cdr, () => {
          this.snapshotAdUnits();
          this.adTagGenerator = adTagLinker(this.adUnits, {$state: this.$state});
          this.bulkAction = this.adUnitBulkActionService.createBulkAction(this.adUnits);
        });
      }
    });
    this.adUnits.makeQuery?.();
  }

  private snapshotAdUnits(): void {
    this.displayItems = (this.adUnits.items || []).slice();
    this.hasItems = this.displayItems.length > 0;
    this.adUnitsEmpty = _.isEmpty(this.displayItems);
    this.adUnitsCount = this.displayItems.length;
    this.selAdUnit = this.displayItems[0];
  }

  private updateSiteHost(): void {
    this.siteHost = '';
    if (!this.model?.siteUrl) {
      return;
    }
    try {
      this.siteHost = new URL(this.model.siteUrl).hostname;
    } catch (e) {
      // ignore invalid site URL
    }
  }

  private getDomainFromUrl(url: string): string {
    if (!url || url.indexOf('http') !== 0) {
      return '';
    }
    return new URL(url).hostname;
  }
}
